import { useState, useEffect, useCallback, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { seatsAPI } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { cn } from '../lib/utils'
import {
  MapPin, Plus, Trash2, Edit2, Search, Power, PowerOff,
  RotateCcw, Copy, X, Move,
  Armchair, Beer, Theater, DoorOpen, Package, Tag,
  Square,
} from 'lucide-react'

// ─── Constants ────────────────────────────────────────────
const CELL   = 28   // px per grid cell in editor
const GRID_W = 40
const GRID_H = 26

const uid = () => Math.random().toString(36).slice(2, 10)

const PALETTE = [
  { type: 'table',    icon: Armchair, label: 'Tafel',   color: 'blue',   w: 3,  h: 2 },
  { type: 'bar',      icon: Beer,     label: 'Toog',    color: 'amber',  w: 14, h: 3 },
  { type: 'stage',    icon: Theater,  label: 'Podium',  color: 'purple', w: 10, h: 4 },
  { type: 'entrance', icon: DoorOpen, label: 'Ingang',  color: 'green',  w: 4,  h: 2 },
  { type: 'zone',     icon: Package,  label: 'Zone',    color: 'gray',   w: 8,  h: 4 },
  // Muur — solid block, dun. Standaard horizontaal (10×1); via de bestaande
  // "roteer"-knop op het details-paneel kan je 'm verticaal zetten voor
  // scheidingswanden. Gebruik meerdere achter elkaar voor een hoek/kamer.
  { type: 'wall',     icon: Square,   label: 'Muur',    color: 'gray',   w: 10, h: 1 },
  { type: 'text',     icon: Tag,      label: 'Label',   color: 'muted',  w: 5,  h: 1 },
]

// Kleur-palet voor seatplan-blokken. Hex-based zodat er niet afgehankelijk is
// van de HUD-token-collapse (waar --neon-blue en --neon-purple naar acid zijn
// gemapt en dus visueel identiek waren). Rendering gebruikt inline styles die
// uit de hex een transparent surface + accent border + text kleur bouwen.
const COLORS = {
  blue:    { hex: '#3b82f6', label: 'Blauw' },
  cyan:    { hex: '#06b6d4', label: 'Cyaan' },
  teal:    { hex: '#14b8a6', label: 'Teal' },
  green:   { hex: '#22c55e', label: 'Groen' },
  lime:    { hex: '#84cc16', label: 'Lime' },
  amber:   { hex: '#f59e0b', label: 'Amber' },
  orange:  { hex: '#f97316', label: 'Oranje' },
  red:     { hex: '#ef4444', label: 'Rood' },
  pink:    { hex: '#ec4899', label: 'Roze' },
  purple:  { hex: '#a855f7', label: 'Paars' },
  indigo:  { hex: '#6366f1', label: 'Indigo' },
  gray:    { hex: '#94a3b8', label: 'Grijs' },
  muted:   { hex: '#64748b', label: 'Gedempt' },
}
const colorStyles = (key) => {
  const c = COLORS[key] || COLORS.gray
  return {
    surface: { backgroundColor: `${c.hex}26`, borderColor: `${c.hex}66` },  // 15%/40%
    text:    { color: c.hex },
    solid:   { backgroundColor: `${c.hex}40`, borderColor: `${c.hex}80`, color: c.hex },
  }
}

const parseConfig = (raw) => {
  try {
    const c = JSON.parse(raw || '{}')
    if (c.version === 2 && Array.isArray(c.blocks)) return c
    // Legacy v1 config → start fresh canvas
    return { version: 2, gridW: GRID_W, gridH: GRID_H, blocks: [] }
  } catch {
    return { version: 2, gridW: GRID_W, gridH: GRID_H, blocks: [] }
  }
}

// Increment a label by offset: "A1"→"A2", "B6"→"B7", "A"→"B", "Z"→"AA",
// "AA"→"AB", "AZ"→"BA", "TABLE"→"TABLE2".
// Belangrijk: NOOIT wrappen (dat gaf een oneindige loop in de add-loop).
const incrementLabel = (base, offset) => {
  if (offset === 0) return base
  const match = base.match(/^(.*?)(\d+)$/)
  if (match) return match[1] + (parseInt(match[2]) + offset)
  // Excel-stijl alfa-serie: elke stap +1, "Z" → "AA", "AZ" → "BA".
  if (/^[A-Z]+$/.test(base.toUpperCase())) {
    let s = base.toUpperCase()
    for (let n = 0; n < offset; n++) {
      let i = s.length - 1
      const chars = s.split('')
      while (i >= 0) {
        if (chars[i] === 'Z') { chars[i] = 'A'; i--; continue }
        chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1)
        break
      }
      s = i < 0 ? 'A' + chars.join('') : chars.join('')
    }
    return s
  }
  return base + (offset + 1)
}

// ─── Seat Assign Modal ───────────────────────────────────
function SeatAssignModal({ seat, currentName, currentUserId, claimId, hasCrown, onConfirm, onClear, onToggleCrown, onCancel }) {
  const [mode, setMode] = useState(currentUserId ? 'user' : 'manual')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [selected, setSelected] = useState(null)
  const [manual, setManual] = useState(currentUserId ? '' : (currentName || ''))
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [mode])

  useEffect(() => {
    if (mode !== 'user' || !query.trim()) { setResults([]); return }
    const t = setTimeout(async () => {
      try { const r = await seatsAPI.searchUsers(query); setResults(r.data) } catch {}
    }, 300)
    return () => clearTimeout(t)
  }, [query, mode])

  const initials = u => ((u.display_name || u.username) || '?').slice(0, 2).toUpperCase()
  const canSave = mode === 'user' ? !!selected : !!manual.trim()

  const save = () => {
    if (!canSave) return
    onConfirm(mode === 'user'
      ? { user_id: selected.id, reserved_name: null }
      : { user_id: null, reserved_name: manual.trim() })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div className="bg-card border border-border rounded-xl p-5 w-80 shadow-xl space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neon-blue">Tafel {seat}</h3>
          <div className="flex items-center gap-1">
            {currentUserId && (
              <Link to={`/spelers/${currentUserId}`}
                    onClick={onCancel}
                    className="text-[10px] px-2 py-1 rounded text-neon-blue border border-neon-blue/30 hover:bg-neon-blue/10 no-underline">
                Open profiel →
              </Link>
            )}
            <button onClick={onCancel} className="p-1 rounded text-muted-foreground hover:bg-accent"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="flex gap-1 bg-muted/30 rounded-lg p-1">
          {['user', 'manual'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={cn('flex-1 text-xs py-1.5 rounded-md transition-all',
                mode === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {m === 'user' ? 'Gebruiker' : 'Handmatig'}
            </button>
          ))}
        </div>

        {mode === 'user' ? (
          <div className="space-y-2">
            {selected ? (
              <div className="flex items-center gap-2 p-2 bg-neon-blue/10 border border-neon-blue/20 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-neon-blue/20 flex items-center justify-center text-xs font-bold text-neon-blue shrink-0">
                  {initials(selected)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{selected.display_name || selected.username}</p>
                  <p className="text-xs text-muted-foreground">@{selected.username}</p>
                </div>
                <button onClick={() => { setSelected(null); setQuery('') }}
                  className="p-1 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
                    placeholder="Zoek gebruiker..."
                    className="w-full pl-8 pr-3 py-2 bg-background border border-border rounded-lg text-sm" />
                </div>
                {results.length > 0 && (
                  <div className="border border-border rounded-lg overflow-hidden max-h-44 overflow-y-auto">
                    {results.map(u => {
                      // Als user al op de map staat: label tonen zodat admin
                      // bewust kiest tussen "verplaatsen" en "andere kiezen".
                      const zit = u.current_table_label
                        ? `${u.current_table_label}${/^[A-Za-z]{1,3}$/.test(u.current_table_label) && u.current_seat_number ? u.current_seat_number : ''}`
                        : null
                      return (
                        <button key={u.id} onClick={() => { setSelected(u); setQuery(''); setResults([]) }}
                          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent text-left border-b border-border/30 last:border-0">
                          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                            {initials(u)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm leading-tight truncate">{u.display_name || u.username}</p>
                            <p className="text-xs text-muted-foreground">@{u.username}</p>
                          </div>
                          {zit && (
                            <span className="font-mono text-[10px] uppercase tracking-widest text-neon-blue/80 shrink-0 border border-neon-blue/30 rounded px-1.5 py-0.5"
                                  title={`Zit al op ${zit} — bevestigen verplaatst deze user`}>
                              🪑 {zit}
                            </span>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
                {query.trim() && results.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-1">Geen gebruikers gevonden</p>
                )}
              </>
            )}
          </div>
        ) : (
          <input ref={mode === 'manual' ? inputRef : null}
            value={manual} onChange={e => setManual(e.target.value)}
            placeholder="Naam / nickname"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
            onKeyDown={e => e.key === 'Enter' && save()} />
        )}

        <div className="flex items-center gap-2 pt-1">
          {(currentName || currentUserId) && (
            <button onClick={onClear}
              className="px-3 py-1.5 rounded-lg text-xs text-destructive hover:bg-destructive/10 border border-destructive/30">
              Wis naam
            </button>
          )}
          {/* Kroontje-toggle — enkel zichtbaar als er al een claim is
              (crown zit op de claim-rij, dus zonder claim geen toggle). */}
          {claimId && (
            <button onClick={onToggleCrown}
              title={hasCrown ? 'Kroontje weghalen' : 'Kroontje geven'}
              className={cn(
                'px-3 py-1.5 rounded-lg text-xs border transition-colors',
                hasCrown
                  ? 'bg-gold/20 border-gold/60 text-gold hover:bg-gold/30'
                  : 'border-border text-muted-foreground hover:text-gold hover:border-gold/40'
              )}>
              👑 {hasCrown ? 'Kroontje af' : 'Kroontje'}
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-accent">
              Annuleer
            </button>
            <button onClick={save} disabled={!canSave}
              className="px-3 py-1.5 rounded-lg text-sm bg-neon-blue/10 border border-neon-blue/20 text-neon-blue hover:bg-neon-blue/20 disabled:opacity-50">
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Table Place Modal ───────────────────────────────────
function TablePlaceModal({ suggestedLabel, onConfirm, onCancel }) {
  const [label, setLabel]           = useState(suggestedLabel || 'A')
  const [tableCount, setTableCount] = useState(1)
  const [orientation, setOrientation] = useState('h')

  const confirm = () => label.trim() && onConfirm({ label, tableCount, orientation })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onCancel}>
      <div className="bg-card border border-border rounded-xl p-5 w-60 shadow-xl space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-neon-blue flex items-center gap-2"><Armchair className="h-4 w-4" />Nieuwe tafel</h3>
        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-muted-foreground block mb-0.5">Eerste label</label>
              <input autoFocus value={label}
                onChange={e => setLabel(e.target.value.toUpperCase().slice(0, 4))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm font-mono tracking-wide"
                onKeyDown={e => e.key === 'Enter' && confirm()} />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Aantal</label>
              <input type="number" value={tableCount} min={1} max={26}
                onChange={e => setTableCount(Math.max(1, Math.min(26, parseInt(e.target.value) || 1)))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-muted-foreground block mb-0.5">Oriëntatie</label>
            <div className="flex gap-1">
              {[['h', '↔ Horizontaal'], ['v', '↕ Verticaal']].map(([o, l]) => (
                <button key={o} onClick={() => setOrientation(o)}
                  className={cn('flex-1 py-1.5 rounded-md border text-[11px] transition-all',
                    orientation === o
                      ? 'bg-neon-blue/15 border-neon-blue/40 text-neon-blue font-semibold'
                      : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent')}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {tableCount > 1 && (
            <p className="text-muted-foreground/60 leading-relaxed">
              Labels: {Array.from({ length: Math.min(tableCount, 6) }, (_, i) =>
                incrementLabel(label, i)
              ).join(', ')}{tableCount > 6 ? ', ...' : ''}
            </p>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-accent">Annuleer</button>
          <button onClick={confirm} disabled={!label.trim()}
            className="px-3 py-1.5 rounded-lg text-sm bg-neon-blue/10 border border-neon-blue/20 text-neon-blue hover:bg-neon-blue/20 disabled:opacity-50">
            {tableCount > 1 ? `${tableCount}× Plaatsen` : 'Plaatsen'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Single Block ────────────────────────────────────────
function Block({ block, claimMap, highlightKeys, isEdit, isSelected, onPointerDown, onSelect, onTableClick }) {
  const palEntry = PALETTE.find(p => p.type === block.type)
  const colorKey = block.color || palEntry?.color || 'gray'
  const styles   = colorStyles(colorKey)
  const bLeft = block.x * CELL, bTop = block.y * CELL
  const bW = block.w * CELL, bH = block.h * CELL
  const isTable = block.type === 'table'
  const isWall  = block.type === 'wall'
  const claim  = isTable && block.tableId ? claimMap[block.tableId] : null
  const name   = claim?.reserved_name || claim?.display_name || claim?.username
  const hl     = claim && highlightKeys?.has(block.tableId)
  const isUser = !!claim?.user_id

  return (
    <div
      style={{ position: 'absolute', left: bLeft, top: bTop, width: bW, height: bH, touchAction: 'none' }}
      onPointerDown={isEdit ? e => { onSelect(block.id); onPointerDown(e, block) } : undefined}
      onClick={!isEdit && isTable && onTableClick ? e => { e.stopPropagation(); onTableClick(block) } : undefined}
      className={cn('select-none', isEdit && 'cursor-grab active:cursor-grabbing',
        !isEdit && isTable && onTableClick && 'cursor-pointer',
        isSelected && 'ring-2 ring-neon-blue/80 ring-offset-background rounded-sm z-20')}>
      <div style={{
             position: 'absolute', inset: 0,
             ...(hl || name ? {} : (isWall ? styles.solid : styles.surface)),
           }}
        className={cn('border flex items-center justify-center overflow-hidden gap-0.5 p-1',
          isWall ? 'rounded-none border-2' : 'rounded-sm',
          block.orientation === 'v' ? 'flex-row' : 'flex-col',
          hl    ? 'bg-neon-green/25 border-neon-green/60'
               : name ? (isUser ? 'bg-neon-blue/25 border-neon-blue/50' : 'bg-neon-blue/15 border-neon-blue/35')
                      : '',
          !isEdit && isTable && 'hover:brightness-110')}>
        <span style={{
                ...(block.orientation === 'v' ? { writingMode: 'vertical-lr' } : {}),
                ...(hl || name ? {} : styles.text),
              }}
          className={cn('text-xs font-bold leading-tight truncate text-center inline-flex items-center gap-1',
            hl ? 'text-neon-green' : name ? 'text-neon-blue' : '')}>
          {!isTable && palEntry?.icon && (() => {
            // palEntry.icon is een React-component (Lucide). Vroeger stond
            // hier `palEntry?.icon + ' '` — dat cast de component naar
            // string → "[object Object]". Renderen als element.
            const Icon = palEntry.icon
            return <Icon className="h-3 w-3 shrink-0" />
          })()}
          {block.label}
        </span>
        {name && (
          <span style={block.orientation === 'v' ? { writingMode: 'vertical-lr' } : {}}
            className={cn('text-[9px] font-medium leading-tight truncate text-center px-1',
              claim?.has_crown && 'text-gold',
              !claim?.has_crown && (hl ? 'text-neon-green/80' : 'text-neon-blue/80'))}>
            {claim?.has_crown && <span className="mr-0.5">👑</span>}{name}
          </span>
        )}
      </div>
      {/* Kroontje-glow rond de tafel als has_crown is gezet */}
      {claim?.has_crown && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
             className="rounded-sm ring-1 ring-gold/70 shadow-[0_0_12px_2px_rgb(var(--gold-rgb)/0.35)]" />
      )}
    </div>
  )
}

// ─── Floor Canvas ────────────────────────────────────────
function FloorCanvas({ config, claims, isEdit, selectedId, onSelect, onBlocksChange, onTableClick, placing, onCanvasPlace, highlightKeys }) {
  const canvasRef = useRef(null)
  const dragRef   = useRef(null)
  const [blocks, setBlocks] = useState(config.blocks || [])
  const [dragging, setDragging]     = useState(null)
  const [cursorGrid, setCursorGrid] = useState(null)

  useEffect(() => { setBlocks(config.blocks || []) }, [config.blocks])

  const gridW = config.gridW || GRID_W
  const gridH = config.gridH || GRID_H

  const claimMap = {}
  for (const c of claims) claimMap[c.table_id] = c

  const toGrid = (clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    return {
      x: Math.max(0, Math.min(gridW - 1, Math.floor((clientX - rect.left) / CELL))),
      y: Math.max(0, Math.min(gridH - 1, Math.floor((clientY - rect.top)  / CELL))),
    }
  }

  const handleBlockPointerDown = (e, block) => {
    if (!isEdit) return
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    const g = toGrid(e.clientX, e.clientY)
    dragRef.current = { id: block.id, offX: g.x - block.x, offY: g.y - block.y }
    setDragging(block.id)
  }

  const handlePointerMove = (e) => {
    const g = toGrid(e.clientX, e.clientY)
    setCursorGrid(g)
    if (!dragRef.current) return
    setBlocks(prev => {
      const b = prev.find(b => b.id === dragRef.current.id)
      if (!b) return prev
      const nx = Math.max(0, Math.min(gridW - b.w, g.x - dragRef.current.offX))
      const ny = Math.max(0, Math.min(gridH - b.h, g.y - dragRef.current.offY))
      return prev.map(bl => bl.id === dragRef.current.id ? { ...bl, x: nx, y: ny } : bl)
    })
  }

  const handlePointerUp = () => {
    if (dragRef.current) {
      setBlocks(prev => { onBlocksChange(prev); return prev })
      dragRef.current = null
      setDragging(null)
    }
  }

  const handleCanvasClick = (e) => {
    if (e.target !== canvasRef.current) return
    onSelect(null)
    if (placing && cursorGrid) onCanvasPlace(cursorGrid)
  }

  const ghost = placing && cursorGrid
    ? { ...PALETTE.find(p => p.type === placing), x: cursorGrid.x, y: cursorGrid.y }
    : null

  return (
    <div className="overflow-auto rounded-xl border-2 border-border">
      <div
        ref={canvasRef}
        style={{
          position: 'relative',
          width:  gridW * CELL,
          height: gridH * CELL,
          backgroundImage: isEdit
            ? `linear-gradient(to right,rgba(255,255,255,0.03) 1px,transparent 1px),
               linear-gradient(to bottom,rgba(255,255,255,0.03) 1px,transparent 1px)`
            : 'none',
          backgroundSize: `${CELL}px ${CELL}px`,
        }}
        className={cn('bg-card/80', placing ? 'cursor-crosshair' : '')}
        onPointerMove={isEdit ? handlePointerMove : undefined}
        onPointerUp={isEdit ? handlePointerUp : undefined}
        onClick={handleCanvasClick}>

        {blocks.map(block => (
          <Block
            key={block.id}
            block={block}
            claimMap={claimMap}
            highlightKeys={highlightKeys}
            isEdit={isEdit}
            isSelected={block.id === selectedId}
            onPointerDown={handleBlockPointerDown}
            onSelect={onSelect}
            onTableClick={onTableClick} />
        ))}

        {ghost && (
          <div style={{ position: 'absolute', left: ghost.x * CELL, top: ghost.y * CELL, width: ghost.w * CELL, height: ghost.h * CELL }}
            className="border-2 border-dashed border-neon-blue/60 bg-neon-blue/10 rounded pointer-events-none" />
        )}
      </div>
    </div>
  )
}

// ─── Properties Panel ───────────────────────────────────
function PropertiesPanel({ block, onUpdate, onDelete, onDuplicate }) {
  if (!block) return (
    <div className="w-44 shrink-0 rounded-xl border border-border/40 bg-card/40 flex items-center justify-center p-4 min-h-[120px]">
      <p className="text-[11px] text-muted-foreground text-center leading-relaxed">Klik een blok om te bewerken</p>
    </div>
  )

  const pal = PALETTE.find(p => p.type === block.type)
  const set = (key, val) => onUpdate({ ...block, [key]: val })

  return (
    <div className="w-44 shrink-0 rounded-xl border border-border bg-card p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold inline-flex items-center gap-1">{pal?.icon ? <pal.icon className="h-3 w-3" /> : null} {pal?.label}</span>
        <div className="flex items-center gap-0.5">
          <button onClick={() => onDuplicate(block.id)} title="Kopieer blok"
            className="p-1 rounded text-muted-foreground hover:text-neon-blue hover:bg-neon-blue/10">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onDelete(block.id)} title="Verwijder blok"
            className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="space-y-2 text-xs">
        <div>
          <label className="text-muted-foreground block mb-0.5">Label</label>
          <input value={block.label || ''} maxLength={block.type === 'table' ? 3 : 32}
            onChange={e => set('label', block.type === 'table' ? e.target.value.toUpperCase() : e.target.value)}
            className="w-full bg-background border border-border rounded px-2 py-1 text-xs" />
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <div>
            <label className="text-muted-foreground block mb-0.5">Breed</label>
            <input type="number" value={block.w} min={1} max={GRID_W}
              onChange={e => set('w', Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full bg-background border border-border rounded px-2 py-1 text-xs" />
          </div>
          <div>
            <label className="text-muted-foreground block mb-0.5">Hoog</label>
            <input type="number" value={block.h} min={1} max={GRID_H}
              onChange={e => set('h', Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full bg-background border border-border rounded px-2 py-1 text-xs" />
          </div>
        </div>

        {block.type === 'table' && (
          <div>
            <label className="text-muted-foreground block mb-0.5">Oriëntatie</label>
            <select value={block.orientation || 'h'} onChange={e => {
              const o = e.target.value
              onUpdate({ ...block, orientation: o, w: o === 'v' ? 2 : 3, h: o === 'v' ? 3 : 2 })
            }}
              className="w-full bg-background border border-border rounded px-2 py-1 text-xs">
              <option value="h">↔ Horizontaal</option>
              <option value="v">↕ Verticaal</option>
            </select>
          </div>
        )}

        <div>
          <label className="text-muted-foreground block mb-0.5">Kleur</label>
          <div className="grid grid-cols-7 gap-1">
            {Object.entries(COLORS).map(([key, { hex, label }]) => {
              const on = (block.color || pal?.color || 'gray') === key
              return (
                <button key={key} type="button"
                        onClick={() => set('color', key)}
                        title={label}
                        style={{ backgroundColor: `${hex}40`, borderColor: on ? hex : `${hex}66` }}
                        className={cn('h-7 rounded border-2 transition-transform hover:scale-110',
                                      on && 'ring-2 ring-offset-1 ring-offset-background',
                                      on && 'scale-105')}>
                  <span className="sr-only">{label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <p className="text-[9px] text-muted-foreground/40 border-t border-border/30 pt-1">Wijzigingen slaan direct op</p>
    </div>
  )
}

// ─── Block Palette ───────────────────────────────────────
function BlockPalette({ placing, onSelect }) {
  return (
    <div className="w-36 shrink-0 space-y-1">
      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1 pb-1">Blokken</p>
      {PALETTE.map(({ type, icon: Icon, label, color }) => {
        const st = colorStyles(color)
        return (
          <button key={type} onClick={() => onSelect(placing === type ? null : type)}
            style={placing === type ? { ...st.surface, ...st.text } : undefined}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-all text-left',
              placing === type
                ? 'font-semibold'
                : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent'
            )}>
            <Icon className="h-3.5 w-3.5" />
            <span className="truncate">{label}</span>
          </button>
        )
      })}
      {placing && (
        <button onClick={() => onSelect(null)}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs border border-dashed border-border/60 text-muted-foreground hover:bg-accent mt-1">
          <X className="h-3 w-3" /> Annuleer
        </button>
      )}
      <p className="text-[9px] text-muted-foreground/40 px-1 pt-2 leading-relaxed">
        Klik blok → klik canvas om te plaatsen
      </p>
    </div>
  )
}

// ─── Admin Layout Panel ──────────────────────────────────
function AdminPanel({ layouts, onCreateLayout, onActivate, onDeactivateAll, onDuplicate, onDeleteLayout, onResetClaims }) {
  const [newName, setNewName] = useState('')
  const [saving, setSaving]   = useState(false)

  const handleCreate = async () => {
    if (!newName.trim()) return
    setSaving(true)
    try { await onCreateLayout(newName.trim()); setNewName('') } finally { setSaving(false) }
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <h3 className="text-sm font-semibold text-neon-purple">Layouts</h3>
      <div className="flex gap-2">
        <input value={newName} onChange={e => setNewName(e.target.value)}
          placeholder="Naam (bv. LAN Mei 2026)"
          className="flex-1 bg-background border border-border rounded-lg px-3 py-1.5 text-sm"
          onKeyDown={e => e.key === 'Enter' && handleCreate()} />
        <button onClick={handleCreate} disabled={saving || !newName.trim()}
          className="px-3 py-1.5 rounded-lg bg-neon-purple/10 border border-neon-purple/20 text-neon-purple hover:bg-neon-purple/20 disabled:opacity-50">
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2 max-h-56 overflow-y-auto">
        {layouts.map(l => (
          <div key={l.id} className={cn('flex items-center gap-2 rounded-lg px-3 py-2 text-sm border',
            l.is_active ? 'bg-neon-blue/10 border-neon-blue/30' : 'bg-muted/30 border-border/50')}>
            <div className="flex-1 min-w-0">
              <span className="font-medium truncate block">{l.name}</span>
              <span className="text-xs text-muted-foreground">{l.table_count} tafels</span>
            </div>
            {l.is_active && <span className="text-xs text-neon-blue font-semibold shrink-0">Actief</span>}
            <div className="flex items-center gap-0.5 shrink-0">
              {!l.is_active && (
                <button onClick={() => onActivate(l.id)} title="Activeer"
                  className="p-1.5 rounded text-muted-foreground hover:text-neon-blue hover:bg-neon-blue/10">
                  <Power className="h-3.5 w-3.5" />
                </button>
              )}
              {l.is_active && (
                <button onClick={onDeactivateAll} title="Deactiveer"
                  className="p-1.5 rounded text-neon-blue hover:bg-muted">
                  <PowerOff className="h-3.5 w-3.5" />
                </button>
              )}
              <button onClick={() => onResetClaims(l.id)} title="Wis namen"
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => onDuplicate(l.id)} title="Kopieer"
                className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted">
                <Copy className="h-3.5 w-3.5" />
              </button>
              {!l.is_active && (
                <button onClick={() => onDeleteLayout(l.id)} title="Verwijder"
                  className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {layouts.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Nog geen layouts</p>}
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────
export default function SeatsPage() {
  const { can } = useAuth()
  const isAdmin = can('manage_seats')
  const navigate = useNavigate()

  const [layout, setLayout]   = useState(null)
  const [tables, setTables]   = useState([])
  const [claims, setClaims]   = useState([])
  const [config, setConfig]   = useState({ version: 2, gridW: GRID_W, gridH: GRID_H, blocks: [] })
  const [loading, setLoading] = useState(true)
  const [editMode, setEditMode]     = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [placing, setPlacing]       = useState(null)
  const [tableModal, setTableModal] = useState(null)
  const [seatModal, setSeatModal]   = useState(null)
  const [showAdmin, setShowAdmin]   = useState(false)
  const [layouts, setLayouts]       = useState([])
  const [saving, setSaving]         = useState(false)
  const [error, setError]           = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const tableUpdateTimer = useRef(null)

  const fetchActive = useCallback(async () => {
    try {
      const { layout: l, tables: t, claims: c } = (await seatsAPI.getActive()).data
      setLayout(l); setTables(t); setClaims(c)
      if (l) setConfig(parseConfig(l.config))
    } catch {} finally { setLoading(false) }
  }, [])

  const fetchLayouts = useCallback(async () => {
    try { setLayouts((await seatsAPI.getLayouts()).data) } catch {}
  }, [])

  useEffect(() => { fetchActive() }, [fetchActive])
  useEffect(() => { if (isAdmin && showAdmin) fetchLayouts() }, [isAdmin, showAdmin, fetchLayouts])

  const saveConfig = useCallback(async (nc) => {
    if (!layout) return
    try { await seatsAPI.updateLayoutConfig(layout.id, nc) } catch {}
  }, [layout])

  // Returnt de save-Promise zodat callers (bv. handleTableModalConfirm) er op
  // kunnen wachten vóór ze `fetchActive()` triggeren. Zonder deze await werd
  // de fetch soms klaar VÓÓR de save doorkwam → server retourneerde de oude
  // config → net-geplaatste block verdween van de canvas (bug: "tafel 2x
  // slepen"), terwijl de bijbehorende seat_tables row wél al aangemaakt was.
  const handleBlocksChange = (newBlocks) => {
    const nc = { ...config, blocks: newBlocks }
    setConfig(nc)
    return saveConfig(nc)
  }

  const handleBlockUpdate = (updated) => {
    const nc = { ...config, blocks: config.blocks.map(b => b.id === updated.id ? updated : b) }
    setConfig(nc)
    saveConfig(nc)
    if (updated.type === 'table' && updated.tableId) {
      if (tableUpdateTimer.current) clearTimeout(tableUpdateTimer.current)
      tableUpdateTimer.current = setTimeout(() => {
        seatsAPI.updateTable(updated.tableId, updated.label, 1)
          .then(fetchActive).catch(() => {})
      }, 800)
    }
  }

  const handleDeleteBlock = async (blockId) => {
    const block = config.blocks.find(b => b.id === blockId)
    // Voor tafels: eerst backend-tafel wissen, daarna refresh tables-lijst.
    // Voor niet-tafels (zone, bar, stage, entrance, text): enkel local blocks
    // filteren + save. Vroeger draaide fetchActive() ALTIJD in dezelfde tick
    // als de save-in-flight — race gooide de gewiste zone terug op canvas.
    if (block?.type === 'table' && block.tableId) {
      try { await seatsAPI.deleteTable(block.tableId) } catch {}
    }
    const nc = { ...config, blocks: config.blocks.filter(b => b.id !== blockId) }
    setConfig(nc)
    setSelectedId(null)
    // Await save zodat een parallelle fetch niet de oude versie terugtrekt.
    try { if (layout) await seatsAPI.updateLayoutConfig(layout.id, nc) } catch {}
    if (block?.type === 'table') fetchActive()
  }

  const handleCanvasPlace = async (gridPos) => {
    if (!placing) return
    if (placing === 'table') {
      const used = tables.map(t => t.label)
      const next = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').find(l => !used.includes(l)) || 'A'
      setTableModal({ ...gridPos, suggestedLabel: next })
    } else {
      const pal = PALETTE.find(p => p.type === placing)
      const nb = { id: uid(), type: placing, x: gridPos.x, y: gridPos.y, w: pal.w, h: pal.h, label: pal.label, color: pal.color }
      handleBlocksChange([...config.blocks, nb])
    }
    setPlacing(null)
  }

  const handleTableModalConfirm = async ({ label, tableCount = 1, orientation = 'h' }) => {
    if (!layout || !tableModal) return
    setSaving(true)
    try {
      const usedLabels = new Set(tables.map(t => t.label))
      const newBlocks  = [...config.blocks]
      const blockW = orientation === 'v' ? 2 : 3
      const blockH = orientation === 'v' ? 3 : 2
      let currentLabel = label

      // Rij-conventie: als het basis-label puur alpha is (bv. "A") én er
      // worden meerdere tafels tegelijk geplaatst, hernoemt de eerste naar
      // "A1", "A2", "A3"… i.p.v. door te lopen tot "B", "C"… Zo staat één
      // rij van tafels onder één rij-letter.
      const isRij = tableCount > 1 && /^[A-Za-z]+$/.test(label.trim())
      const baseAlfa = isRij ? label.trim().toUpperCase() : null

      for (let i = 0; i < tableCount; i++) {
        currentLabel = isRij ? `${baseAlfa}${i + 1}` : incrementLabel(label, i)
        // Safety-cap: incrementLabel is nu wrap-vrij, maar we willen ook
        // vroeger stoppen als de rest van de code ooit dubbele labels
        // teruggeeft. Zonder cap freezet de browser (had een bug).
        let attempts = 0
        while (i > 0 && usedLabels.has(currentLabel) && attempts < 500) {
          currentLabel = incrementLabel(currentLabel, 1)
          attempts++
        }
        const bx = orientation === 'v'
          ? tableModal.x
          : Math.min(tableModal.x + i * blockW, GRID_W - blockW)
        const by = orientation === 'v'
          ? Math.min(tableModal.y + i * blockH, GRID_H - blockH)
          : tableModal.y
        const res = await seatsAPI.addTable(layout.id, currentLabel, 1)
        const tableId = res.data.id
        newBlocks.push({ id: uid(), type: 'table', x: bx, y: by, w: blockW, h: blockH, label: currentLabel, tableId, color: 'blue', orientation })
        usedLabels.add(currentLabel)
      }

      await handleBlocksChange(newBlocks)   // wacht op save vóór fetch
      await fetchActive()
    } catch (e) {
      setError(e.response?.data?.error || 'Fout bij aanmaken tafel')
      setTimeout(() => setError(null), 3000)
    } finally {
      setSaving(false)
      setTableModal(null)
    }
  }

  const handleDuplicateBlock = async (blockId) => {
    const source = config.blocks.find(b => b.id === blockId)
    if (!source) return
    const nx = Math.min(source.x + source.w, GRID_W - source.w)
    if (source.type === 'table' && layout) {
      setSaving(true)
      try {
        const usedLabels = tables.map(t => t.label)
        const nextLabel  = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').find(l => !usedLabels.includes(l)) || (source.label + '2')
        const res = await seatsAPI.addTable(layout.id, nextLabel, 1)
        const nb  = { ...source, id: uid(), tableId: res.data.id, label: nextLabel, x: nx }
        await handleBlocksChange([...config.blocks, nb])   // save eerst
        await fetchActive()
      } catch (e) {
        setError(e.response?.data?.error || 'Fout bij kopiëren')
        setTimeout(() => setError(null), 3000)
      } finally { setSaving(false) }
    } else {
      handleBlocksChange([...config.blocks, { ...source, id: uid(), x: nx }])
    }
  }

  const handleTableClick = (block) => {
    const claim = claims.find(c => c.table_id === block.tableId)
    // Deelnemer klikt op tafel met user-claim → naar dat profiel.
    // (Handmatig-reservation zonder user_id opent niets voor deelnemers.)
    if (!isAdmin) {
      if (claim?.user_id) navigate(`/spelers/${claim.user_id}`)
      return
    }
    // Admin: seat-modal blijft de norm voor snelle claim-aanpassing.
    setSeatModal({
      tableId: block.tableId,
      seatNum: 1,
      label: block.label,
      name: claim?.reserved_name || claim?.display_name || claim?.username,
      claimId: claim?.id,
      userId: claim?.user_id,
      hasCrown: !!claim?.has_crown,
    })
  }

  const handleToggleCrown = async () => {
    if (!seatModal?.claimId) return
    try {
      const r = await seatsAPI.toggleCrown(seatModal.claimId)
      setSeatModal(m => m ? { ...m, hasCrown: !!r.data?.has_crown } : m)
      fetchActive()
    } catch (e) {
      setError(e.response?.data?.error || 'Fout')
      setTimeout(() => setError(null), 3000)
    }
  }

  const handleSeatConfirm = async ({ user_id, reserved_name }) => {
    if (!seatModal) return
    try {
      if (seatModal.claimId) await seatsAPI.removeClaimById(seatModal.claimId)
      await seatsAPI.adminReserve(seatModal.tableId, seatModal.seatNum, reserved_name, user_id)
      await fetchActive()
    } catch (e) {
      setError(e.response?.data?.error || 'Fout')
      setTimeout(() => setError(null), 3000)
    }
    setSeatModal(null)
  }

  const handleSeatClear = async () => {
    if (!seatModal?.claimId) return setSeatModal(null)
    try { await seatsAPI.removeClaimById(seatModal.claimId); fetchActive() } catch {}
    setSeatModal(null)
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (['Delete', 'Backspace'].includes(e.key) && selectedId && editMode && e.target === document.body) {
        handleDeleteBlock(selectedId)
      }
      if (e.key === 'Escape') { setPlacing(null); setSelectedId(null) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [selectedId, editMode, config])

  // Highlight matching seats
  const highlightKeys = searchQuery.trim()
    ? new Set(claims
        .filter(c => (c.reserved_name || c.display_name || c.username || '').toLowerCase().includes(searchQuery.toLowerCase()))
        .map(c => c.table_id))
    : new Set()

  const namedSeats = claims.filter(c => c.reserved_name || c.user_id).length
  const totalSeats = tables.length
  const selectedBlock = config.blocks.find(b => b.id === selectedId)

  const handleActivate     = async (id) => { try { await seatsAPI.activateLayout(id);  fetchLayouts(); fetchActive() } catch {} }
  const handleDeactivateAll= async ()   => { try { await seatsAPI.deactivateAll();      fetchLayouts(); fetchActive() } catch {} }
  const handleDuplicate    = async (id) => { try { await seatsAPI.duplicateLayout(id); fetchLayouts() } catch {} }
  const handleDeleteLayout = async (id) => {
    if (!confirm('Layout verwijderen?')) return
    try { await seatsAPI.deleteLayout(id); fetchLayouts(); fetchActive() } catch {}
  }
  const handleResetClaims = async (id) => {
    if (!confirm('Alle namen wissen?')) return
    try { await seatsAPI.resetAllClaims(id); fetchActive() } catch {}
  }
  const handleCreateLayout = async (name) => {
    try { await seatsAPI.createLayout(name); fetchLayouts() } catch {}
  }

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Laden...</div>

  return (
    <div className="space-y-5">
      {seatModal && (
        <SeatAssignModal
          seat={seatModal.label}
          currentName={seatModal.name}
          currentUserId={seatModal.userId}
          claimId={seatModal.claimId}
          hasCrown={seatModal.hasCrown}
          onConfirm={handleSeatConfirm}
          onClear={handleSeatClear}
          onToggleCrown={handleToggleCrown}
          onCancel={() => setSeatModal(null)} />
      )}
      {tableModal && (
        <TablePlaceModal
          suggestedLabel={tableModal.suggestedLabel}
          onConfirm={handleTableModalConfirm}
          onCancel={() => setTableModal(null)} />
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" /> Zitplaatsen
          </h1>
          {layout
            ? <p className="text-sm text-muted-foreground mt-1">{layout.name} — {namedSeats}/{totalSeats} toegewezen</p>
            : <p className="text-sm text-muted-foreground mt-1">Geen actieve plattegrond</p>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {layout && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Zoek naam..."
                className="pl-8 pr-3 py-1.5 bg-background border border-border rounded-lg text-sm w-36" />
            </div>
          )}
          {isAdmin && layout && (
            <button onClick={() => { setEditMode(v => !v); setShowAdmin(false); setSelectedId(null); setPlacing(null) }}
              className={cn('text-xs px-3 py-1.5 rounded-lg border transition-all flex items-center gap-1.5',
                editMode
                  ? 'bg-acid/10 border-acid/30 text-acid'
                  : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent')}>
              <Edit2 className="h-3.5 w-3.5" /> {editMode ? 'Klaar' : 'Bewerken'}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => { setShowAdmin(v => !v); setEditMode(false) }}
              className={cn('text-xs px-3 py-1.5 rounded-lg border transition-all',
                showAdmin
                  ? 'bg-peach/10 border-peach/30 text-peach'
                  : 'border-border text-muted-foreground hover:text-foreground hover:bg-accent')}>
              {showAdmin ? 'Sluit' : 'Layouts'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {editMode && (
        <div className="text-xs text-acid bg-acid/5 border border-acid/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <Move className="h-3.5 w-3.5 shrink-0" />
          Sleep blokken om te verplaatsen · klik blok = selecteren ·{' '}
          <kbd className="bg-muted px-1 rounded text-[10px]">Delete</kbd> verwijdert selectie ·{' '}
          <kbd className="bg-muted px-1 rounded text-[10px]">Esc</kbd> annuleert
        </div>
      )}

      {isAdmin && showAdmin && (
        <AdminPanel
          layouts={layouts}
          onCreateLayout={handleCreateLayout}
          onActivate={handleActivate}
          onDeactivateAll={handleDeactivateAll}
          onDuplicate={handleDuplicate}
          onDeleteLayout={handleDeleteLayout}
          onResetClaims={handleResetClaims} />
      )}

      {!layout ? (
        <div className="flex flex-col items-center justify-center h-52 text-muted-foreground gap-2 border-2 border-dashed border-border rounded-2xl">
          <MapPin className="h-10 w-10 opacity-20" />
          <p className="text-sm">Geen actieve plattegrond</p>
          {isAdmin && <p className="text-xs opacity-60">Maak een layout aan via Layouts</p>}
        </div>
      ) : (
        <div className={cn('flex gap-3 items-start', editMode ? '' : 'flex-col')}>
          {editMode && (
            <BlockPalette placing={placing} onSelect={p => { setPlacing(p); setSelectedId(null) }} />
          )}

          <div className="flex-1 min-w-0 space-y-2 overflow-hidden">
            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-4 rounded-sm bg-neon-blue/20 border border-neon-blue/40" />
                <span>Account</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-4 rounded-sm bg-neon-blue/10 border border-neon-blue/30" />
                <span>Handmatig</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-4 rounded-sm border border-border/30" />
                <span>Vrij</span>
              </div>
              {highlightKeys.size > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-4 rounded-sm bg-neon-green/25 border border-neon-green/60" />
                  <span>Gevonden</span>
                </div>
              )}
              <span className="ml-auto text-muted-foreground/40">{layout.name}</span>
            </div>

            <FloorCanvas
              config={config}
              claims={claims}
              isEdit={editMode}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onBlocksChange={handleBlocksChange}
              onTableClick={handleTableClick}
              placing={placing}
              onCanvasPlace={handleCanvasPlace}
              highlightKeys={highlightKeys} />

            {config.blocks.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed border-border/30 rounded-xl -mt-2">
                {isAdmin && editMode
                  ? 'Klik een blok in de zijbalk, dan op het canvas om te plaatsen'
                  : 'Plattegrond is nog leeg'}
              </div>
            )}
          </div>

          {editMode && (
            <PropertiesPanel
              block={selectedBlock}
              onUpdate={handleBlockUpdate}
              onDelete={handleDeleteBlock}
              onDuplicate={handleDuplicateBlock} />
          )}
        </div>
      )}
    </div>
  )
}
